import { useRef, useState } from 'react';

import {
  bookingPhotosKey,
  deleteBookingPhoto,
  useBookingPhotos,
  type BookingPhoto,
  type GarmentPhotoPhase,
} from '@/api/bookings';
import { useApiMutation } from '@/api/mutations';
import { uploadMedia } from '@/api/upload';
import { MediaViewer } from '@/components/MediaViewer';
import { Notice } from '@/components/PageHeading';

/**
 * Photographing a garment at the counter, as it goes out or as it comes back (spec Section 9).
 *
 * <p>
 * <strong>This is what makes the issue guard real.</strong> The server refuses a handover of
 * garments nobody photographed unless the counter waives it out loud — and until this existed there
 * was no way to satisfy that check, only to waive it. A photograph taken here is what settles an
 * argument about a mark six days later.
 * </p>
 *
 * <p>
 * <strong>`capture` on the file input is the point of the whole component.</strong> On a phone or a
 * tablet at the counter it opens the camera directly rather than a file browser, because the
 * photograph does not exist yet — nobody is picking an existing file at the moment a customer is
 * standing there holding a sherwani. On a desktop the attribute is ignored and it behaves as an
 * ordinary picker, which is the right fallback.
 * </p>
 *
 * <p>
 * Uploads go straight to the object store, as everywhere else in the product: presign, PUT, confirm.
 * The count the guard reads is bumped by the confirm, in the same save as the row.
 * </p>
 */
export function ConditionPhotos({
  bookingId,
  garmentId,
  serialCode,
  phase,
  disabled = false,
}: {
  bookingId: number;
  garmentId: number;
  /** What the viewer calls this garment, and what the alt text says. */
  serialCode: string;
  phase: GarmentPhotoPhase;
  disabled?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);

  const input = useRef<HTMLInputElement>(null);

  const photos = useBookingPhotos(bookingId);

  const remove = useApiMutation<void, BookingPhoto>({
    mutationFn: (row) => deleteBookingPhoto(bookingId, garmentId, row.id),
    success: 'Photograph removed.',
    invalidates: [bookingPhotosKey],
  });

  // This garment, this end of the rental. Showing the issue set while somebody books a garment back
  // in would be showing them the wrong evidence at the moment it matters most.
  const mine = (photos.data ?? []).filter(
    (photo) => photo.garmentId === garmentId && photo.phase === phase,
  );

  async function send(files: FileList) {
    setError(null);
    setBusy(true);

    try {
      for (const file of Array.from(files)) {
        await uploadMedia(
          `/api/booking/${bookingId}/garment/${garmentId}/photo`,
          file,
          undefined,
          undefined,
          { phase },
        );
      }

      await photos.refetch();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The photograph could not be uploaded.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        {mine.map((photo, index) => (
          <span key={photo.id} className="group relative">
            <button
              type="button"
              onClick={() => setViewing(index)}
              aria-label={`View ${photo.fileName}`}
              className="border-border-color block size-10 cursor-pointer overflow-hidden rounded border bg-light"
            >
              {photo.mediaType === 'Video' ? (
                <span className="text-default flex size-full items-center justify-center">
                  <i className="icon icon-play text-[13px]" />
                </span>
              ) : (
                <img src={photo.url} alt={photo.caption ?? ''} className="size-full object-cover" />
              )}
            </button>

            {!disabled && (
              <button
                type="button"
                onClick={() => void remove.mutateAsync(photo).catch(() => undefined)}
                aria-label={`Remove ${photo.fileName}`}
                className="bg-danger absolute -end-1 -top-1 hidden size-4 cursor-pointer items-center justify-center rounded-full text-white group-hover:flex"
              >
                <i className="icon icon-x text-[9px]" />
              </button>
            )}
          </span>
        ))}

        <input
          ref={input}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,video/mp4,video/webm,video/quicktime"
          // Opens the camera on a phone or tablet, which is the device this is used on. Ignored on
          // a desktop, where it falls back to an ordinary picker.
          capture="environment"
          multiple
          className="hidden"
          onChange={(event) => {
            if (event.target.files && event.target.files.length > 0) {
              void send(event.target.files);
            }

            event.target.value = '';
          }}
        />

        <button
          type="button"
          disabled={disabled || busy}
          onClick={() => input.current?.click()}
          className={`border-border-color flex size-10 items-center justify-center rounded border border-dashed ${
            mine.length === 0 ? 'text-warning bg-warning-transparent' : 'text-default bg-white'
          } cursor-pointer disabled:cursor-not-allowed disabled:opacity-50`}
          aria-label={`Photograph ${serialCode}`}
        >
          <i className={`icon ${busy ? 'icon-loader' : 'icon-camera'} text-[15px]`} />
        </button>
      </div>

      {error && (
        <Notice tone="danger" title="The photograph did not upload">
          {error}
        </Notice>
      )}

      {viewing !== null && mine.length > 0 && (
        <MediaViewer
          items={mine.map((photo) => ({
            id: photo.id,
            url: photo.url,
            mediaType: photo.mediaType,
            fileName: photo.fileName,
            caption: photo.caption,
            note: phase === 'Out' ? 'Taken at issue' : 'Taken at return',
          }))}
          startAt={viewing}
          title={serialCode}
          onClose={() => setViewing(null)}
        />
      )}
    </>
  );
}
